/**
 * Invoicing: what was billed, what arrived, and what is still owed.
 *
 * Three figures are kept deliberately distinct and never added together.
 *
 *   expected  — what a placement was agreed to bill, held on the placement
 *   invoiced  — what has actually been claimed from the client
 *   received  — what has actually arrived
 *
 * They diverge for real reasons: a placement may not be invoiced yet, an
 * invoice may be part-paid, a dropped placement inside its guarantee window
 * may never be billed at all. Collapsing them into one "revenue" number is the
 * same mistake the overview avoids by carrying no profit column.
 *
 * Overdue and ageing are computed by the API at read time and are never stored,
 * so nothing here recomputes them from dates on the client.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Loader2,
  Plus,
  Receipt,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { client as api, errorMessage } from '../../api/client';
import { money } from '../../lib/money';
import { isCompanyAdmin, useAuth } from '../../store/auth';
import InvoiceForm from '../../components/InvoiceForm';
import InvoiceTrackerTable from '../../components/InvoiceTrackerTable';
import type {
  Invoice,
  InvoiceStatus,
  InvoiceWithPayments,
  Page,
  ReceivablesSummary,
} from '../../types';

const STATUS_TONE: Record<InvoiceStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  raised: 'bg-blue-100 text-blue-700',
  partially_paid: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-500',
  written_off: 'bg-gray-200 text-gray-500',
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'draft',
  raised: 'raised',
  partially_paid: 'part paid',
  paid: 'paid',
  overdue: 'overdue',
  cancelled: 'cancelled',
  written_off: 'written off',
};

/** Statuses a user can filter by. Excludes the two the API never writes. */
const FILTERABLE: InvoiceStatus[] = [
  'draft',
  'raised',
  'partially_paid',
  'paid',
  'cancelled',
];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function BillingPage() {
  const queryClient = useQueryClient();
  // Reads are open to managers because chasing payment is their job, but every
  // write here is company-admin. Hide the controls rather than let them 403.
  const me = useAuth((s) => s.user);
  const canEdit = isCompanyAdmin(me);
  const [showForm, setShowForm] = useState(false);
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  /**
   * Which shape the same rows are shown in.
   *
   * Two views rather than two pages. A separate Invoices page would need its
   * own creation form, and two forms over one endpoint drift apart.
   */
  const [view, setView] = useState<'summary' | 'tracker'>('summary');
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
    queryClient.invalidateQueries({ queryKey: ['receivables'] });
  };

  const summary = useQuery<ReceivablesSummary>({
    queryKey: ['receivables'],
    queryFn: async () => (await api.get('/api/v1/admin/billing/receivables')).data,
  });

  const invoices = useQuery<Page<Invoice>>({
    queryKey: ['invoices', statusFilter, overdueOnly],
    queryFn: async () =>
      (await api.get('/api/v1/admin/billing/invoices', {
        params: {
          limit: 200,
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(overdueOnly ? { overdueOnly: true } : {}),
        },
      })).data,
  });

  const createInvoice = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post('/api/v1/admin/billing/invoices', body)).data,
    onSuccess: (invoice: Invoice) => {
      invalidate();
      setShowForm(false);
      setOpenInvoiceId(invoice.id);
      toast.success('Draft invoice created');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = invoices.data?.items ?? [];

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Billing</h2>
            <p className="mt-1 max-w-2xl text-xs text-gray-500">
              An invoice is created as a draft and can be corrected freely. Once
              raised, the client has the figures, so they stop being editable
              and a correction means cancelling and issuing a new one.
            </p>
          </div>
          {canEdit && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowForm((v) => !v)}
            >
              <Plus className="h-4 w-4" />
              {showForm ? 'Cancel' : 'New invoice'}
            </button>
          )}
        </div>
      </section>

      {canEdit && showForm && (
        <InvoiceForm
          requirePlacement={view === 'tracker'}
          submitting={createInvoice.isPending}
          onSubmit={(body) => createInvoice.mutate(body)}
        />
      )}

      {summary.data && <ReceivablesPanel summary={summary.data} />}

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-900">Invoices</h2>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-medium text-gray-600">
              Status
              <select
                className="input mt-1 block"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | '')}
              >
                <option value="">All</option>
                {FILTERABLE.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 pb-2 text-xs text-gray-600">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={overdueOnly}
                onChange={(e) => setOverdueOnly(e.target.checked)}
              />
              Overdue only
            </label>
          </div>
        </div>

        {/* Overdue is derived, so it cannot be filtered in the query. The
            server narrows the returned page rather than the whole book — the
            totals above are the figure computed across everything. */}
        {overdueOnly && (
          <p className="text-xs text-gray-500">
            Filtered from the most recent invoices. The totals above cover every
            invoice.
          </p>
        )}

        {invoices.isLoading ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : invoices.isError ? (
          <div className="card flex items-center justify-between border border-red-200 bg-red-50">
            <p className="text-sm text-red-800">Could not load invoices.</p>
            <label className="text-xs text-gray-500">
              View
              <select
                className="input ml-2 py-1 text-xs"
                value={view}
                onChange={(e) => setView(e.target.value as 'summary' | 'tracker')}
              >
                <option value="summary">Summary</option>
                <option value="tracker">Invoice tracker</option>
              </select>
            </label>
            <button type="button" className="btn-ghost" onClick={() => void invoices.refetch()}>
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="card text-center">
            <Receipt className="mx-auto h-6 w-6 text-gray-300" />
            <p className="mt-2 text-sm text-gray-600">No invoices to show.</p>
          </div>
        ) : view === 'tracker' ? (
          <InvoiceTrackerTable
            invoices={rows}
            onOpen={(invoice) => setOpenInvoiceId(invoice.id)}
          />
        ) : (
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th scope="col" className="px-4 py-2.5 font-medium">Number</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Client</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Dated</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Due</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Total</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Received</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Outstanding</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setOpenInvoiceId(invoice.id)}
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-gray-900">
                      {invoice.invoiceNumber}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{invoice.clientName ?? '—'}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                      {invoice.invoiceDate}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                      {invoice.dueDate ?? '—'}
                      {invoice.isOverdue && (
                        <span className="ml-1.5 text-xs font-medium text-red-600">
                          +{invoice.daysOverdue}d
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-900">
                      {money(invoice.totalAmount)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-600">
                      {money(invoice.amountReceived)}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-gray-900">
                      {money(invoice.outstanding)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <span className={`badge ${STATUS_TONE[invoice.status]}`}>
                        {STATUS_LABELS[invoice.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {openInvoiceId && (
        <InvoiceDrawer
          invoiceId={openInvoiceId}
          canEdit={canEdit}
          onClose={() => setOpenInvoiceId(null)}
          onChanged={invalidate}
        />
      )}
    </div>
  );
}

function ReceivablesPanel({ summary }: { summary: ReceivablesSummary }) {
  return (
    <section className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label="Invoiced" value={money(summary.invoiced)} />
        <Tile label="Received" value={money(summary.received)} />
        <Tile label="Outstanding" value={money(summary.outstanding)} />
        <Tile
          label="Overdue"
          value={money(summary.overdue)}
          tone={summary.overdue !== '0.00' ? 'text-red-600' : undefined}
        />
      </div>

      <div className="rounded-xl bg-white p-3 ring-1 ring-gray-200">
        <h3 className="text-xs font-medium uppercase text-gray-500">
          Outstanding By Age
        </h3>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {summary.ageing.map((bucket) => (
            <div key={bucket.bucket}>
              <p className="text-xs text-gray-500">
                {bucket.bucket === 'current' ? 'Not yet due' : `${bucket.bucket} days`}
              </p>
              <p className="tabular-nums text-sm text-gray-900">{money(bucket.amount)}</p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Drafts and cancelled invoices are excluded — neither is a claim on
          anyone. Figures cover all {summary.invoiceCount} live invoices, not
          just the page below.
        </p>
      </div>
    </section>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="rounded-xl bg-white p-3 ring-1 ring-gray-200">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-0.5 text-lg font-semibold tabular-nums ${tone ?? 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  );
}


function InvoiceDrawer({
  invoiceId,
  canEdit,
  onClose,
  onChanged,
}: {
  invoiceId: string;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [showCancel, setShowCancel] = useState(false);
  const [reason, setReason] = useState('');

  const invoice = useQuery<InvoiceWithPayments>({
    queryKey: ['invoice', invoiceId],
    queryFn: async () =>
      (await api.get(`/api/v1/admin/billing/invoices/${invoiceId}`)).data,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
    onChanged();
  };

  const raise = useMutation({
    mutationFn: async () =>
      (await api.post(`/api/v1/admin/billing/invoices/${invoiceId}/raise`)).data,
    onSuccess: () => {
      refresh();
      toast.success('Invoice raised');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const cancel = useMutation({
    mutationFn: async () =>
      (await api.post(`/api/v1/admin/billing/invoices/${invoiceId}/cancel`, { reason }))
        .data,
    onSuccess: () => {
      refresh();
      setShowCancel(false);
      toast.success('Invoice cancelled');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const pay = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post(`/api/v1/admin/billing/invoices/${invoiceId}/payments`, body)).data,
    onSuccess: () => {
      refresh();
      toast.success('Payment recorded');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const data = invoice.data;
  const settled = data
    && (data.status === 'paid' || data.status === 'cancelled');

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onClick={onClose}>
      <div
        className="h-full w-full max-w-lg overflow-y-auto bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-mono text-sm text-gray-900">
              {data?.invoiceNumber ?? 'Invoice'}
            </h2>
            {data && (
              <p className="mt-0.5 text-xs text-gray-500">
                {data.clientName} · dated {data.invoiceDate}
              </p>
            )}
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {invoice.isLoading || !data ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <Figure label="Total" value={money(data.totalAmount)} />
              <Figure label="Received" value={money(data.amountReceived)} />
              {Number(data.tdsWithheld) > 0 && (
                <Figure label="TDS withheld" value={money(data.tdsWithheld)} />
              )}
              {data.expectedReceipt && (
                <Figure label="Expected after TDS" value={money(data.expectedReceipt)} />
              )}
              <Figure label="Outstanding" value={money(data.outstanding)} />
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <span className={`badge mt-1 ${STATUS_TONE[data.status]}`}>
                  {STATUS_LABELS[data.status]}
                </span>
                {data.isOverdue && (
                  <span className="badge ml-1 mt-1 bg-red-100 text-red-700">
                    {data.daysOverdue}d overdue
                  </span>
                )}
              </div>
            </div>

            {canEdit && data.status === 'draft' && (
              <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200">
                <p className="text-sm text-gray-700">
                  This is still a draft. Raising it is what makes it a claim on
                  the client — after that the figures are fixed.
                </p>
                <button
                  type="button"
                  className="btn-primary mt-2"
                  onClick={() => raise.mutate()}
                  disabled={raise.isPending}
                >
                  {raise.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  <FileText className="h-4 w-4" />
                  Raise invoice
                </button>
              </div>
            )}

            <section>
              <h3 className="text-sm font-semibold text-gray-900">Payments</h3>
              {data.payments.length === 0 ? (
                <p className="mt-1 text-sm text-gray-500">Nothing received yet.</p>
              ) : (
                <ul className="mt-2 divide-y divide-gray-100 rounded-xl ring-1 ring-gray-200">
                  {data.payments.map((p) => (
                    <li key={p.id} className="flex items-baseline justify-between px-3 py-2">
                      <span className="text-sm text-gray-600">
                        {p.receivedOn}
                        {p.method && <span className="ml-1 text-xs text-gray-400">{p.method}</span>}
                        {p.reference && (
                          <span className="ml-1 font-mono text-xs text-gray-400">
                            {p.reference}
                          </span>
                        )}
                      </span>
                      <span className="tabular-nums text-sm text-gray-900">
                        {money(p.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {canEdit && !settled && data.status !== 'draft' && (
                <PaymentForm
                  maxAmount={data.outstanding}
                  minDate={data.invoiceDate}
                  submitting={pay.isPending}
                  onSubmit={pay.mutate}
                />
              )}
            </section>

            {canEdit && !settled && (
              <section className="border-t border-gray-100 pt-4">
                {showCancel ? (
                  <div className="space-y-2">
                    <label className="label" htmlFor="cancel-reason">
                      Why Is This Being Cancelled?
                    </label>
                    <textarea
                      spellCheck={true}
                      id="cancel-reason"
                      className="input"
                      rows={2}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <p className="text-xs text-gray-500">
                      Recorded in the audit log — a claim that vanished with no
                      stated reason is what an auditor asks about.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={reason.trim().length < 3 || cancel.isPending}
                        onClick={() => cancel.mutate()}
                      >
                        {cancel.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                        Cancel invoice
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => setShowCancel(false)}
                      >
                        Keep it
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn-ghost text-red-600"
                    onClick={() => setShowCancel(true)}
                  >
                    Cancel this invoice
                  </button>
                )}
                {Number(data.amountReceived) > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Payments have been received, so this can no longer be
                    cancelled.
                  </p>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="tabular-nums text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}

function PaymentForm({
  maxAmount,
  minDate,
  submitting,
  onSubmit,
}: {
  maxAmount: string;
  minDate: string;
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    amount: '',
    tdsWithheld: '',
    receivedOn: today(),
    method: '',
    reference: '',
  });

  return (
    <form
      className="mt-3 space-y-2 rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          amount: form.amount,
          // Sent as 0 rather than null when blank: most clients withhold
          // nothing, and the server treats absent and zero the same way.
          tdsWithheld: form.tdsWithheld.trim() || '0',
          receivedOn: form.receivedOn,
          method: form.method.trim() || null,
          reference: form.reference.trim() || null,
        });
      }}
    >
      <h4 className="text-sm font-medium text-gray-900">Record a Payment</h4>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label" htmlFor="p-amount">Amount</label>
          <input
            spellCheck={false}
            id="p-amount"
            className="input font-mono"
            inputMode="decimal"
            placeholder="0.00"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            {money(maxAmount)} outstanding, counting anything withheld.
          </p>
        </div>
        <div>
          <label className="label" htmlFor="p-tds">TDS Withheld</label>
          <input
            spellCheck={false}
            id="p-tds"
            className="input font-mono"
            inputMode="decimal"
            placeholder="0.00"
            value={form.tdsWithheld}
            onChange={(e) => setForm({ ...form, tdsWithheld: e.target.value })}
          />
          <p className="mt-1 text-xs text-gray-500">
            {/* Said plainly, because it is the thing people get wrong: the
                invoice is settled by the two together, not by the cash. */}
            What the client kept back and paid to the government. Counts
            towards settling the invoice.
          </p>
        </div>
        <div>
          <label className="label" htmlFor="p-date">Received On</label>
          <input
            id="p-date"
            type="date"
            className="input"
            value={form.receivedOn}
            min={minDate}
            onChange={(e) => setForm({ ...form, receivedOn: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="p-method">Method</label>
          <input
            spellCheck={false}
            id="p-method"
            className="input"
            placeholder="NEFT"
            value={form.method}
            onChange={(e) => setForm({ ...form, method: e.target.value })}
          />
        </div>
        <div>
          <label className="label" htmlFor="p-ref">Reference</label>
          <input
            spellCheck={false}
            id="p-ref"
            className="input font-mono"
            placeholder="UTR"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
          />
        </div>
      </div>
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        Record payment
      </button>
    </form>
  );
}
